import { PLANS } from '../plans.js';
import { AVAILABLE_LANGUAGES, getHomeCopy, getLanguage, getSidebarCopy, setLanguage } from '../i18n.js';
import { startDemoSession } from '../auth.js';

const html = String.raw;

const quotaToText = (plan, pricingDetails = {}) => {
  const quota = plan.quota ?? {};
  const quotaCopy = pricingDetails.quota ?? {};
  if (quota.type === 'monthly') {
    if (quota.value) {
      return `${quota.value} ${quotaCopy.monthly ?? quotaCopy.default ?? ''}`.trim();
    }
    return quotaCopy.monthly ?? quotaCopy.default ?? '';
  }
  if (quota.type === 'per_job') {
    return quotaCopy.per_job ?? quotaCopy.default ?? '';
  }
  if (quota.type === 'unlimited') {
    return quotaCopy.unlimited ?? quotaCopy.default ?? '';
  }
  return quotaCopy.default ?? '';
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const extractFirstRequirement = (rawText) => {
  if (!rawText) return null;
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return lines[0] ?? null;
};

const tryParseJsonRequirement = (rawText) => {
  try {
    const parsed = JSON.parse(rawText);
    if (Array.isArray(parsed)) {
      const firstString = parsed.find((item) => typeof item === 'string' && item.trim().length > 0);
      if (firstString) {
        return firstString.trim();
      }
      const firstObject = parsed.find((item) => item && typeof item === 'object');
      if (firstObject) {
        const candidate = firstObject.requirement ?? firstObject.description ?? Object.values(firstObject)[0];
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }
    }
    if (parsed && typeof parsed === 'object') {
      const requirementList =
        parsed.requirements ??
        parsed.items ??
        parsed.data ??
        (Array.isArray(parsed.results) ? parsed.results : null);
      if (Array.isArray(requirementList)) {
        const firstString = requirementList.find((item) => typeof item === 'string' && item.trim());
        if (firstString) {
          return firstString.trim();
        }
        const firstObject = requirementList.find((item) => item && typeof item === 'object');
        if (firstObject) {
          const candidate = firstObject.requirement ?? firstObject.description ?? Object.values(firstObject)[0];
          if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
          }
        }
      }
      const candidate = parsed.requirement ?? parsed.description;
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    if (typeof parsed === 'string' && parsed.trim()) {
      return parsed.trim();
    }
  } catch (error) {
    return null;
  }
  return null;
};

const tryParseCsvRequirement = (rawText) => {
  const rows = rawText.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);
  if (rows.length === 0) return null;
  const [firstRow, secondRow] = rows;
  if (!secondRow) {
    return firstRow.split(';')[0]?.split(',')[0]?.trim() ?? null;
  }
  const header = firstRow.split(/[,;]/).map((column) => column.trim().toLowerCase());
  const data = secondRow.split(/[,;]/).map((column) => column.trim());
  const requirementIndex =
    header.findIndex((column) => column.includes('anforderung') || column.includes('requirement')) ?? -1;
  if (requirementIndex >= 0 && data[requirementIndex]) {
    return data[requirementIndex];
  }
  return data.find((value) => value.length > 0) ?? null;
};

const findFirstRequirement = async (file) => {
  const text = await file.text();
  const extension = file.name?.split('.').pop()?.toLowerCase() ?? '';
  if (extension === 'json') {
    return tryParseJsonRequirement(text);
  }
  if (extension === 'csv') {
    return tryParseCsvRequirement(text);
  }
  const mimeType = file.type.toLowerCase();
  if (mimeType.includes('json')) {
    return tryParseJsonRequirement(text);
  }
  if (mimeType.includes('csv')) {
    return tryParseCsvRequirement(text);
  }
  return extractFirstRequirement(text);
};

const renderPlans = (pricingDetails = {}) => {
  const billingRequired = pricingDetails.billingRequired ?? '';
  const billingFree = pricingDetails.free ?? '';
  const actionLabel = pricingDetails.actionLabel ?? 'Tarif wählen';
  const unitPriceLabel = pricingDetails.unitPriceLabel ?? '€3 / Nutzung';
  return html`
    <div class="plan-grid">
      ${PLANS.filter((plan) => plan.visibleInStore !== false)
        .map(
          (plan) => html`
            <article class="plan-card" data-plan-id="${plan.id}">
              <div class="badge">${plan.name}</div>
              <strong class="plan-price">${unitPriceLabel}</strong>
              <p class="muted">${plan.description}</p>
              <p class="muted tiny">${quotaToText(plan, pricingDetails)}</p>
              <div class="stack">
                <span class="muted tiny">
                  ${plan.requiresBilling ? billingRequired : billingFree}
                </span>
              </div>
              <a class="button plan-action" href="#/register?plan=${encodeURIComponent(plan.id)}">
                ${actionLabel}
              </a>
            </article>
          `,
        )
        .join('')}
    </div>
  `;
};


export const renderHomeView = (container) => {
  const copy = getHomeCopy();
  const sidebarCopy = getSidebarCopy();
  const languageLabels = sidebarCopy.languages ?? {};
  // Marketing: keep optional arrays safe across locales.
  const trustItems = Array.isArray(copy.trust) ? copy.trust : [];
  const steps = Array.isArray(copy.steps) ? copy.steps : [];
  const faqItems = Array.isArray(copy.faq) ? copy.faq : [];
  container.innerHTML = html`
    <div class="stack" id="home-view">
      <section class="hero-card stack home-hero">
        ${copy.heroEyebrow ? html`<p class="hero-eyebrow">${copy.heroEyebrow}</p>` : ''}
        <h1>${copy.heroTitle}</h1>
        <p class="hero-lead">${copy.heroDescription}</p>
        <div class="row hero-actions" style="gap:12px;">
          <button
            type="button"
            class="button"
            data-start-demo
          >
            ${copy.ctas.secondary ?? 'Demo ansehen'}
          </button>
        </div>
        <div class="home-language-selector">
          <span class="muted tiny">${sidebarCopy.languageLabel ?? 'Sprache'}</span>
          <div class="row">
            ${AVAILABLE_LANGUAGES.map(
              (code) => html`
                <button
                  type="button"
                  class="language-pill"
                  data-language-toggle="${code}"
                >
                  ${languageLabels[code] ?? code.toUpperCase()}
                </button>
              `,
            ).join('')}
          </div>
        </div>
        ${trustItems.length
          ? html`
              <div class="trust-strip">
                ${trustItems.map((item) => html`<span class="trust-pill">${item}</span>`).join('')}
              </div>
            `
          : ''}
      </section>

      <section class="section-card stack" id="home-features">
        <div class="section-header-block">
          <h2>${copy.featuresTitle ?? 'Was dich erwartet'}</h2>
          ${copy.featuresSubtitle ? html`<p class="muted">${copy.featuresSubtitle}</p>` : ''}
        </div>
        <div class="feature-grid">
          ${copy.features
            .map(
              (feature) => html`
                <article class="feature-card">
                  <strong>${feature.title}</strong>
                  <p class="muted small">${feature.description}</p>
                </article>
              `,
            )
            .join('')}
        </div>
      </section>

      ${steps.length
        ? html`
            <section class="section-card stack" id="home-steps">
              <div class="section-header-block">
                <h2>${copy.stepsTitle ?? 'So funktioniert es'}</h2>
                ${copy.stepsSubtitle ? html`<p class="muted">${copy.stepsSubtitle}</p>` : ''}
              </div>
              <div class="steps-grid">
                ${steps
                  .map(
                    (step, index) => html`
                      <article class="step-card">
                        <span class="step-index">${index + 1}</span>
                        <strong class="step-title">${step.title}</strong>
                        <p class="muted small">${step.description}</p>
                      </article>
                    `,
                  )
                  .join('')}
              </div>
            </section>
          `
        : ''}

      <section class="section-card stack" id="home-pricing">
        <h2>${copy.pricingTitle}</h2>
        <p class="muted">${copy.pricingSubtitle}</p>
        ${renderPlans(copy.pricingDetails)}
        ${copy.pricingNote ? html`<p class="muted small pricing-note">${copy.pricingNote}</p>` : ''}
      </section>

      <section class="section-card stack" id="home-social-proof">
        <h2>${copy.testimonialsTitle}</h2>
        <p class="muted">${copy.testimonialsSubtitle}</p>
        <div class="plan-grid">
          ${copy.testimonials
            .map(
              (testimonial) => html`
                <article class="plan-card" style="cursor:default;">
                  <p>"${testimonial.quote}"</p>
                  <p class="muted small">${testimonial.author}</p>
                </article>
              `,
            )
            .join('')}
        </div>
        <div class="row home-social-actions">
          <button type="button" class="button" data-start-demo>${copy.ctas.secondary ?? 'Demo ansehen'}</button>
        </div>
      </section>

      ${faqItems.length
        ? html`
            <section class="section-card stack" id="home-faq">
              <h2>${copy.faqTitle ?? 'FAQ'}</h2>
              ${copy.faqSubtitle ? html`<p class="muted">${copy.faqSubtitle}</p>` : ''}
              <div class="faq-list">
                ${faqItems
                  .map(
                    (item) => html`
                      <details class="faq-item">
                        <summary>${item.question}</summary>
                        <p class="muted small">${item.answer}</p>
                      </details>
                    `,
                  )
                  .join('')}
              </div>
            </section>
          `
        : ''}

    </div>
  `;

  container.querySelectorAll('[data-navigate]').forEach((element) => {
    element.addEventListener('click', (event) => {
      const target = event.currentTarget.getAttribute('data-navigate');
      if (target) {
        window.location.hash = target;
      }
    });
  });

  container.querySelectorAll('[data-scroll]').forEach((element) => {
    element.addEventListener('click', (event) => {
      const selector = event.currentTarget.getAttribute('data-scroll');
      if (!selector) return;
      const target = container.querySelector(selector);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  const startDemoButtons = container.querySelectorAll('[data-start-demo]');
  startDemoButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        await startDemoSession();
      } catch (error) {
        console.error('Failed to start demo session', error);
        button.disabled = false;
      }
    });
  });

  const demoForm = container.querySelector('#home-demo-form');
  const resultContainer = container.querySelector('#home-demo-result');
  const fileInput = container.querySelector('#home-demo-file');
  if (demoForm && resultContainer && fileInput) {
    demoForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const [file] = fileInput.files ?? [];
      if (!file) {
        resultContainer.innerHTML = `<p class="muted">${copy.demo.selectFileWarning}</p>`;
        return;
      }

      resultContainer.innerHTML = `<p class="muted">${copy.demo.loading}</p>`;
      try {
        const requirement = await findFirstRequirement(file);
        if (requirement) {
          resultContainer.innerHTML = html`
            <div class="card stack">
              <strong>${copy.demo.successTitle}</strong>
              <p>${escapeHtml(requirement)}</p>
              <p class="muted small">${copy.demo.successNote}</p>
            </div>
          `;
        } else {
          resultContainer.innerHTML = `<p class="muted">${copy.demo.noRequirement}</p>`;
        }
      } catch (error) {
        console.error('Demo parsing failed', error);
        resultContainer.innerHTML = `<p class="muted">${copy.demo.error}</p>`;
      }
    });
  }

  const homeLanguageButtons = Array.from(
    container.querySelectorAll('.home-language-selector [data-language-toggle]'),
  );
  const updateHomeLanguageButtons = () => {
    const current = getLanguage();
    homeLanguageButtons.forEach((button) => {
      button.classList.toggle('active', button.getAttribute('data-language-toggle') === current);
    });
  };
  if (homeLanguageButtons.length) {
    updateHomeLanguageButtons();
    homeLanguageButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.getAttribute('data-language-toggle');
        if (next) {
          setLanguage(next);
          updateHomeLanguageButtons();
        }
      });
    });
  }
};
